
import { Component, Vue } from 'vue-property-decorator'
import { Field } from '@/services/utils/baseForm'
import { popUpRoute } from '@/services/utils/popupRoute'
import { parsePathQueryString } from '@/shared/helpers/parsePathQueryString'
import BaseForm from '@/stories/base-form/BaseForm.vue'
import { Route } from 'vue-router'
import eventBus from '@/eventBus'
import { freezeObject } from '@/shared/helpers/freezeObject'
import { IDiscountProfile } from '@/modules/seller-management/discount-profile/types/discountProfileTypes'
import { discountProfileEndpoints } from '@/modules/seller-management/discount-profile'
import { ContentFormatter } from '@/shared/helpers/contentFormatter'

@Component({
  name: 'TheUpsertDiscountProfile',
  components: {
    DialogBaseSkeleton: () =>
      import(
        /* webpackChunkName: "DialogBaseSkeleton" */ '@/stories/dialog-base-skeleton/DialogBaseSkeleton.vue'
      ),
    BaseForm
  }
})
export default class TheUpsertDiscountProfile extends Vue {
  loading = false
  data = {} as IDiscountProfile
  id = this.$route.params.id || ''
  originalData = {} as IDiscountProfile

  title = (_params: Route['params'], query: Route['query']) =>
    query.id
      ? `${this.$t('dialog.discountProfile.edit')}`
      : `${this.$t('dialog.discountProfile.addNew')}`

  /** function will be passed to child components */
  fields(): Field<IDiscountProfile>[] {
    return [
      Field.text<IDiscountProfile>('limitName', 'profileName')
        .setCol('col-12')
        .required(),
      Field.currency<IDiscountProfile>('fixedAmount', 'fixedAmount')
        .setCol('col-12')
        .required(),
      Field.currency<IDiscountProfile>('percentageAmount', 'percentageAmount')
        .setProps({
          prefix: '%'
        })
        .setProps({ decimalLength: { min: 0, max: 2 } })
        .setRules(`between:0,100`)
        .setCol('col-12')
        .required(),
      Field.textarea<IDiscountProfile>('description', 'description').setCol(
        'col-12'
      )
    ]
  }

  /** submit the data to the desired endpoint. */
  async handleSubmit(
    values: IDiscountProfile,
    _p: Record<string, number>,
    q: Record<string, number>
  ) {
    if (this.originalData.percentageAmount !== values.percentageAmount)
      values.percentageAmount = +(values.percentageAmount / 100).toFixed(4)

    if (q.id) {
      await discountProfileEndpoints
        .patch(q.id, this.originalData, values)
        .then(() => popUpRoute.removeModalPath(['id']))
    } else
      await discountProfileEndpoints.post(values).then(() => {
        popUpRoute.removeModalPath(['id'])
      })
    eventBus.refreshData()
  }

  bindSubmitTitle(_params: Route['params'], query: Route['query']): string {
    return query.id ? 'update' : 'add'
  }

  /** closePopUp function pass to the baseModal component */
  closePopUp() {
    popUpRoute.removeModalPath(['id'])
  }

  created() {
    this.fetchRemoteData()
  }

  /** call given fetch data function */
  async fetchRemoteData() {
    const id = parsePathQueryString(location.search).id
    this.loading = true
    try {
      if (id) {
        this.data = await discountProfileEndpoints.get(id).then(res => {
          //on edit convert to percentage format
          res.percentageAmount = Number(
            ContentFormatter.$formatToPercentage(res.percentageAmount)
          )
          return res
        })
        this.originalData = freezeObject(this.data)
      }
    } catch (error) {
      throw new Error(`Failed to fetch form data. Due to ${error}`)
    } finally {
      this.loading = false
    }
  }
}
